import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { SelectAllHeader } from "@/components/data-table/components/SelectAllHeader";
import { format, eachDayOfInterval, isWeekend } from "date-fns";
import {
  DAY_CELL_WIDTH,
  ROLE_CELL_WIDTH,
  SELECT_CELL_WIDTH,
} from "@/modules/projects/components/schedules/constants";
import { useProjectSchedulesContext } from "@/modules/projects/components/schedules/ProjectSchedulesContext";
import { cn } from "@/lib/utils";
import { ProjectSchedulesVisitPopover } from "@/modules/projects/components/schedules/ProjectSchedulesVisitPopover";
import { useTranslation } from "react-i18next";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import { ScheduleWithVisitMap } from "@/modules/projects/components/schedules/types";
import { useLocale } from "@/hooks/useLocale";

const columnHelper = createColumnHelper<ScheduleWithVisitMap>();

export const useProjectSchedulesColumns = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { selectedStartDate, selectedEndDate, data } =
    useProjectSchedulesContext();

  const rowIds = useMemo(() => data.map((d) => d.id), [data]);

  const fixedColumns = useMemo<ColumnDef<ScheduleWithVisitMap>[]>(
    () => [
      {
        accessorKey: "selectSchedule",
        header: () => <SelectAllHeader rowIds={rowIds} />,
        size: SELECT_CELL_WIDTH,
        meta: {
          className: `sticky left-0 z-10 bg-white text-center pl-0`,
        },
      },
      columnHelper.accessor("project_role_name", {
        header: () => <>{t("projectSchedules.fields.project_role_name")}</>,
        cell: ({ row }) => {
          return (
            <div className="w-full truncate">
              {row.original.project_role_name}
            </div>
          );
        },
        size: ROLE_CELL_WIDTH,
        meta: {
          className: `sticky left-10 z-10 bg-white pl-0`,
        },
      }),
    ],
    [t, rowIds],
  );

  // Create columns for each day in the date range
  const dayColumns = useMemo<ColumnDef<ScheduleWithVisitMap>[]>(() => {
    // Get all days in the date range
    const daysInRange = eachDayOfInterval({
      start: selectedStartDate,
      end: selectedEndDate,
    });

    // Create a column for each day in the range
    return daysInRange.map((date) => {
      const day = date.getDate();
      const dayName = format(date, "EEE", { locale });
      const isWeekendDay = isWeekend(date);
      const formattedDate = format(date, DEFAULT_DATE_FORMAT); // Used as a unique key

      return columnHelper.accessor(
        (row) => row.visitsByDate.get(formattedDate),
        {
          id: `day_${formattedDate}`,
          header: () => (
            <div className={"flex flex-col items-center"}>
              <div className={isWeekendDay ? "font-medium" : ""}>{day}</div>
              <div className={isWeekendDay ? "font-semibold" : ""}>
                {dayName}
              </div>
            </div>
          ),
          cell: (row) => {
            const scheduleVisitData = row.getValue();
            const schedule = row.row.original;

            if (!scheduleVisitData) {
              return <span className="text-slate-400">-</span>;
            }
            return (
              <ProjectSchedulesVisitPopover
                scheduleVisitData={scheduleVisitData}
                scheduleData={schedule}
              />
            );
          },
          meta: {
            className: cn(
              "border-r border-solid border-r-white text-center p-0",
              {
                "bg-blue-50 border-t-2 border-t-blue-200": isWeekendDay,
              },
            ),
          },
          size: DAY_CELL_WIDTH,
        },
      );
    });
  }, [selectedStartDate, selectedEndDate, columnHelper, locale]);

  // Define all columns including fixed ones and day columns
  const columns = useMemo<ColumnDef<ScheduleWithVisitMap>[]>(
    () => [...fixedColumns, ...dayColumns],
    [fixedColumns, dayColumns],
  );

  return columns;
};
